import { useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, Link } from 'react-router';
import { ThemeToggle } from './ThemeToggle';
import { NotificationBell } from './NotificationBell';
import { CommandBar } from './CommandBar';
import { GLOBAL_NAV_ITEMS } from './globalNav';
import { useAuthStore } from '../store/authStore';
import { useLogout } from '../api/auth';
import { useProjects } from '../api/project';
import { AccountSelect } from './AccountSelect';
import { CreateProjectDrawer } from './CreateProjectDrawer';
import { BrandMark } from './auth/AuthBrandPanel';

// ---------------------------------------------------------------------------
// Rebrand phase 2 — structural chrome. Replaces the phase-1 horizontal
// top-nav link row with the reference's two-part chrome (ref-3 skeleton):
//   1. A fixed-left, full-height icon-only rail: brand mark, hairline,
//      six global destinations on resting --surface-2 icon circles, then a
//      bottom utility cluster (theme toggle + settings gear) after a divider
//      (§2.1) — GLOBAL_NAV_ITEMS is shared with the command palette
//      (see globalNav.tsx / CommandBar.tsx) so the destination list has
//      exactly one source of truth.
//   2. A h64 top bar with no fill of its own (§2.2): breadcrumb/page context
//      + the Projects dropdown on the left; on the right the account filter,
//      the accent Create CTA, the ⌘K search pill, a vertical hairline, the
//      bell, and the user identity cluster (avatar + name + role + chevron)
//      wired to the user menu (§2.6).
// The project-level workspace rail (ProjectSidebar.tsx) is untouched in
// structure — it keeps full text labels on purpose: an icon-only global rail
// paired with an icon-only project rail would leave no textual wayfinding
// anywhere in the app, so the project rail is the discoverability
// counterweight (see ProjectSidebar.tsx's own header comment).
// ---------------------------------------------------------------------------

// Context label shown at the left of the top bar for non-project routes.
// Project routes pass active="" (see App.tsx) and show only the Projects
// dropdown — the project's own name/breadcrumb already renders inside the
// page content (ProjectWorkspacePage's <Breadcrumbs>).
const TOPBAR_CONTEXT_LABEL: Record<string, string> = {
  dashboard: 'Dashboard',
  accounts: 'Accounts',
  schedule: 'Schedule',
  'resources-calendar': 'Resources',
  capacity: 'Capacity',
  support: 'Support',
  settings: 'Settings',
};

function IconProject() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

// Settings — a gear (admin configuration surface).
function IconSettings() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Skip-to-content link — the first focusable element on every page. Visually
// hidden until it receives keyboard focus (standard skip-link pattern); see
// the `.skip-link` utility in index.css for the focus-in transition.
// ---------------------------------------------------------------------------

function SkipLink() {
  return (
    <a href="#main-content" className="skip-link focus-ring">
      Skip to content
    </a>
  );
}

// ---------------------------------------------------------------------------
// Rail tooltip — a plain positioned span, no library. Shown after a 150ms
// hover/focus delay (RailNavItem owns the timer), positioned to the right of
// the rail item since the rail itself is pinned to the left edge.
// ---------------------------------------------------------------------------

function RailTooltip({ label }: { label: string }) {
  return (
    <span
      role="tooltip"
      style={{
        position: 'absolute',
        left: 'calc(100% + 10px)',
        top: '50%',
        transform: 'translateY(-50%)',
        padding: '5px 9px',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--tooltip-bg)',
        color: 'var(--tooltip-fg)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: 'var(--shadow-2)',
        zIndex: 50,
      }}
    >
      {label}
    </span>
  );
}

/** Shared hover/focus-delay state for a rail item's tooltip (150ms, per spec). */
function useTooltipDelay() {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<number | null>(null);

  function schedule() {
    timerRef.current = window.setTimeout(() => setVisible(true), 150);
  }
  function cancel() {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  return { visible, schedule, cancel };
}

// ---------------------------------------------------------------------------
// Global icon rail
// ---------------------------------------------------------------------------

interface RailNavItemProps {
  to: string;
  label: string;
  icon: ReactNode;
  active: boolean;
}

function RailNavItem({ to, label, icon, active }: RailNavItemProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const tooltip = useTooltipDelay();

  return (
    <div style={{ position: 'relative' }}>
      <Link
        to={to}
        aria-label={label}
        aria-current={active ? 'page' : undefined}
        className="focus-ring pressable"
        onMouseEnter={() => { setHovered(true); tooltip.schedule(); }}
        onMouseLeave={() => { setHovered(false); tooltip.cancel(); }}
        onFocus={() => { setFocused(true); tooltip.schedule(); }}
        onBlur={() => { setFocused(false); tooltip.cancel(); }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 'var(--rail-item-hit)',
          height: 'var(--rail-item-hit)',
          boxSizing: 'border-box',
          borderRadius: '50%',
          textDecoration: 'none',
          flexShrink: 0,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'var(--rail-item-visual)',
            height: 'var(--rail-item-visual)',
            borderRadius: '50%',
            // §2.1 — every inactive icon sits on a resting icon-circle fill
            // (the reference "icon circle stack"); hover = +1 ladder step;
            // active = the white-fill inversion. QA R2: --icon-circle-bg (not
            // --surface-2) — --surface-2 was ~1.1:1 against the light rail
            // bg (near-invisible); the token darkens one step in light only.
            background: active ? 'var(--rail-active-bg)' : (hovered || focused) ? 'var(--surface-3)' : 'var(--icon-circle-bg)',
            color: active ? 'var(--rail-active-text)' : (hovered || focused) ? 'var(--rail-hover-text)' : 'var(--text-low)',
            transition: 'background 0.12s var(--ease-out), color 0.12s var(--ease-out)',
          }}
        >
          {icon}
        </span>
      </Link>
      {tooltip.visible && <RailTooltip label={label} />}
    </div>
  );
}

// Present a role KEY (e.g. PROJECT_MANAGER) as friendly text (Project Manager).
function humanizeRole(key: string): string {
  return key
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

/** "jane.doe" / "jane_doe" → "Jane Doe" — display name from the email local part. */
function displayNameFromEmail(email: string | null): string {
  if (!email) return 'Account';
  const local = email.split('@')[0];
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ') || 'Account';
}

/**
 * User identity cluster — top-bar right, per §2.6 / ref-3: avatar circle
 * (flat deep-emerald fill, mint-tint initials), name 13/500 + chevron, role 11
 * below. Opens the user menu (email, role, sign out) as a dropdown.
 */
function UserMenuCluster() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const logout = useLogout();

  const email = useAuthStore((s) => s.email);
  const role = useAuthStore((s) => s.role);

  const initials = (() => {
    if (!email) return 'U';
    const local = email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
    return local.slice(0, 2).toUpperCase() || 'U';
  })();

  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  function handleSignOut() {
    setOpen(false);
    logout.mutate(undefined, { onSettled: () => navigate('/login') });
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-label={email ? `User menu — ${email}` : 'User menu'}
        aria-haspopup="menu"
        aria-expanded={open}
        className="focus-ring pressable"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 44,
          boxSizing: 'border-box',
          padding: '0 2px',
          borderRadius: 'var(--radius-pill)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'var(--avatar-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-sans)',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--avatar-fg)',
            flexShrink: 0,
          }}
        >
          {initials}
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--text-hi)',
              whiteSpace: 'nowrap',
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {displayNameFromEmail(email)}
            <ChevronDown open={open} />
          </span>
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 11,
              color: 'var(--text-low)',
              whiteSpace: 'nowrap',
            }}
          >
            {role ? humanizeRole(role) : '—'}
          </span>
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="pop-in"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 210,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-panel)',
            boxShadow: 'var(--shadow-2)',
            zIndex: 40,
            overflow: 'hidden',
            ['--pop-origin' as string]: 'top right',
          }}
        >
          <div style={{ padding: '10px 12px' }}>
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                fontWeight: 500,
                color: 'var(--text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {email ?? '—'}
            </div>
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-2xs)',
                color: 'var(--text-subtle)',
                letterSpacing: '0.04em',
                marginTop: 2,
              }}
            >
              {role ? humanizeRole(role) : '—'}
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border)' }} />

          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '9px 12px',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-muted)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 0.1s, color 0.1s',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.background = 'var(--health-red-bg)';
              el.style.color = 'var(--health-red)';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.background = 'transparent';
              el.style.color = 'var(--text-muted)';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

interface IconRailProps {
  active: string;
}

function IconRail({ active }: IconRailProps) {
  const location = useLocation();
  const canConfigure = useAuthStore((s) => s.hasAnyPermission('CONFIG_MANAGE', 'ROLE_MANAGE'));
  const isSettingsActive = active === 'settings' || location.pathname === '/settings';

  return (
    <nav
      aria-label="Primary navigation"
      className="no-print"
      style={{
        position: 'fixed',
        top: 10,
        left: 10,
        bottom: 10,
        width: 'var(--rail-w)',
        background: 'var(--rail-bg)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-2)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 14,
        paddingBottom: 14,
        zIndex: 30,
      }}
    >
      {/* §2.1 — brand mark, bare glyph, no container. */}
      <Link
        to="/"
        aria-label="Seedz Manage — go to Dashboard"
        className="focus-ring pressable"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          marginBottom: 14,
          flexShrink: 0,
        }}
      >
        <BrandMark glyphSize={24} wordmark={false} label="Seedz Manage" />
      </Link>

      {/* Hairline divider under the logo mark — §2.1 rail anatomy. */}
      <span aria-hidden="true" style={{ width: '60%', height: 1, background: 'var(--hairline)', marginBottom: 14, flexShrink: 0 }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {GLOBAL_NAV_ITEMS.map((item) => {
          const isActive = item.id === active || location.pathname === item.path;
          return (
            <RailNavItem key={item.id} to={item.path} label={item.label} icon={<item.Icon />} active={isActive} />
          );
        })}
      </div>

      <div style={{ flex: 1 }} />

      {/* §2.1 — bottom utility cluster after a hairline divider: theme
          toggle + (permission-gated) settings gear. The user identity
          cluster lives in the top bar (§2.2/§2.6), not here. */}
      <span aria-hidden="true" style={{ width: '60%', height: 1, background: 'var(--hairline)', marginBottom: 8, flexShrink: 0 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
        <ThemeToggle />
        {canConfigure && (
          <RailNavItem to="/settings" label="Settings" icon={<IconSettings />} active={isSettingsActive} />
        )}
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Search pill — the ⌘K trigger. Global keyboard shortcut is wired in TopBar
// (the one place that owns the CommandBar's open state).
// ---------------------------------------------------------------------------

function SearchPillTrigger({ onClick }: { onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Search — press Command K"
      className="focus-ring pressable"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        height: 36,
        boxSizing: 'border-box',
        // §2.4 — pill w ~250–290; shrinks gracefully on narrow viewports.
        width: 260,
        minWidth: 190,
        flexShrink: 1,
        padding: '0 6px 0 12px',
        borderRadius: 'var(--radius-pill)',
        background: hovered ? 'var(--surface-2)' : 'var(--inset)',
        border: 'none',
        cursor: 'pointer',
        transition: 'background 0.12s',
      }}
    >
      <span style={{ display: 'flex', color: 'var(--text-low)', flexShrink: 0 }}>
        <IconSearch />
      </span>
      <span
        style={{
          flex: 1,
          textAlign: 'left',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
          color: 'var(--text-low)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
      >
        Search destinations, projects…
      </span>
      {/* §2.4 keycap — ~28×20, r6, --surface-3, 11px. */}
      <kbd
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 28,
          height: 20,
          boxSizing: 'border-box',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          fontWeight: 600,
          padding: '0 6px',
          borderRadius: 'var(--radius-kbd)',
          background: 'var(--surface-3)',
          border: 'none',
          color: 'var(--text-mid)',
          flexShrink: 0,
        }}
      >
        ⌘K
      </kbd>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Shared dropdown menu item — used by the Projects menu and the Create menu.
// ---------------------------------------------------------------------------

interface DropdownMenuItemProps {
  label: string;
  icon?: ReactNode;
  active?: boolean;
  onClick: () => void;
}

function DropdownMenuItem({ label, icon, active, onClick }: DropdownMenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '8px 12px',
        background: active ? 'var(--brand-soft)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        fontWeight: active ? 600 : 400,
        color: active ? 'var(--brand)' : 'var(--text)',
        whiteSpace: 'nowrap',
        transition: 'background 0.1s, color 0.1s',
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-inset)';
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      {icon && <span style={{ flexShrink: 0, color: active ? 'var(--brand)' : 'var(--text-subtle)' }}>{icon}</span>}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Projects dropdown — contextual, so it lives in the top bar next to the
// breadcrumb rather than as a global rail destination (it isn't one of the
// six fixed sections; it's "whichever project you're looking at").
// ---------------------------------------------------------------------------

function ProjectsMenu({ isActive }: { isActive: boolean }) {
  const { data: projects = [] } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (wrapperRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
          setAnchor({ top: r.bottom + 6, left: r.left });
          setOpen((v) => !v);
        }}
        className="focus-ring"
        style={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          padding: '0 2px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-sans)',
          fontWeight: 500,
          fontSize: 'var(--text-sm)',
          color: isActive ? 'var(--brand)' : 'var(--text-muted)',
          borderBottom: '2px solid transparent',
          transition: 'color 0.12s, border-color 0.12s, background 0.12s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          if (!isActive) (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)';
        }}
        onMouseLeave={(e) => {
          if (!isActive) (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)';
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '5px 10px',
            borderRadius: 'var(--radius-sm)',
            background: isActive ? 'var(--nav-active-bg)' : 'transparent',
            transition: 'background 0.12s',
          }}
        >
          Projects
          <ChevronDown open={open} />
        </span>
      </button>

      {open && anchor && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="pop-in"
          style={{
            position: 'fixed',
            top: anchor.top,
            left: anchor.left,
            minWidth: 220,
            maxHeight: 320,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-panel)',
            boxShadow: 'var(--shadow-2)',
            zIndex: 30,
            padding: '4px 0',
            ['--pop-origin' as string]: 'top left',
          }}
        >
          {projects.length === 0 && (
            <div style={{ padding: '8px 12px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              No projects
            </div>
          )}
          {projects.map((p) => (
            <DropdownMenuItem
              key={p.id}
              label={p.name}
              icon={<IconProject />}
              active={location.pathname === `/project/${p.id}`}
              onClick={() => {
                setOpen(false);
                navigate(`/project/${p.id}`);
              }}
            />
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create button — the reference's accent CTA pill. Uses the --btn-primary-*
// tokens (emerald fill + --on-accent-class contrast text, per the
// §1.10 light derivation — the light theme keeps the accent CTA rather than
// flipping to solid olive with white text).
//
// Jira-style "create from anywhere": Work item / Task deep-link into a
// project's workspace tabs, so on a project route they navigate straight
// there. Anywhere else there's no project in scope yet, so picking either one
// slides the same menu to a second step — a compact project picker — instead
// of hiding the items. Support ticket has no project dependency and always
// goes straight to /support.
// ---------------------------------------------------------------------------

type CreateKind = 'cr' | 'task';

const CREATE_KIND_TAB: Record<CreateKind, string> = { cr: 'cr-backlog', task: 'board' };

function CreateMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: projects = [] } = useProjects();
  const canManageProject = useAuthStore((s) => s.hasPermission('PROJECT_MANAGE'));
  const [open, setOpen] = useState(false);
  const [pendingKind, setPendingKind] = useState<CreateKind | null>(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setPendingKind(null);
  }, [open]);

  const projectMatch = location.pathname.match(/^\/project\/([^/]+)/);
  const projectId = projectMatch?.[1];

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  function pick(kind: CreateKind) {
    if (projectId) {
      go(`/project/${projectId}?tab=${CREATE_KIND_TAB[kind]}`);
      return;
    }
    setPendingKind(kind);
  }

  function pickProject(id: string) {
    if (!pendingKind) return;
    go(`/project/${id}?tab=${CREATE_KIND_TAB[pendingKind]}`);
  }

  function openCreateProject() {
    setOpen(false);
    setCreatingProject(true);
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="focus-ring pressable"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 38,
          boxSizing: 'border-box',
          padding: '0 16px 0 6px',
          borderRadius: 'var(--radius-pill)',
          background: 'var(--btn-primary-bg)',
          color: 'var(--btn-primary-fg)',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-sans)',
          fontWeight: 600,
          fontSize: 'var(--text-sm)',
          transition: 'background 0.12s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'var(--btn-primary-bg-hover)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'var(--btn-primary-bg)';
        }}
      >
        {/* §2.8 primary-button anatomy — leading solid on-accent circle around the glyph. */}
        <span
          aria-hidden="true"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: 'var(--btn-primary-fg)',
            color: 'var(--btn-primary-bg)',
            flexShrink: 0,
          }}
        >
          <IconPlus />
        </span>
        Create
      </button>

      {open && (
        <div
          role="menu"
          className="pop-in"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 190,
            maxHeight: 320,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-panel)',
            boxShadow: 'var(--shadow-2)',
            zIndex: 30,
            padding: '4px 0',
            ['--pop-origin' as string]: 'top right',
          }}
        >
          {pendingKind ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '2px 8px 6px',
                  marginBottom: 4,
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <button
                  type="button"
                  onClick={() => setPendingKind(null)}
                  className="focus-ring"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 'var(--radius-xs)',
                    padding: '4px 6px',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 500,
                    color: 'var(--text-muted)',
                    transition: 'color 0.12s, background 0.12s',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-inset)';
                    (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)';
                  }}
                >
                  ‹ Back
                </button>
                <span
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-2xs)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: 'var(--text-subtle)',
                  }}
                >
                  Select a project
                </span>
              </div>

              {projects.length === 0 && (
                <div style={{ padding: '8px 12px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
                  No projects yet.
                </div>
              )}
              {projects.map((p) => (
                <DropdownMenuItem key={p.id} label={p.name} icon={<IconProject />} onClick={() => pickProject(p.id)} />
              ))}
            </>
          ) : (
            <>
              {canManageProject && (
                <DropdownMenuItem label="Project" icon={<IconProject />} onClick={openCreateProject} />
              )}
              <DropdownMenuItem label="Work item" onClick={() => pick('cr')} />
              <DropdownMenuItem label="Task" onClick={() => pick('task')} />
              <DropdownMenuItem label="Support ticket" onClick={() => go('/support')} />
            </>
          )}
        </div>
      )}

      <CreateProjectDrawer open={creatingProject} onClose={() => setCreatingProject(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top bar — breadcrumb/context + Projects on the left; account filter,
// Create, search pill, hairline, bell, user identity cluster on the right
// (§2.2 skeleton). Settings + theme live in the rail's utility cluster.
// ---------------------------------------------------------------------------

interface TopBarProps {
  /** Currently active nav item id (drives the rail's active pill + the
   *  breadcrumb context label). Empty string on project routes. */
  active: string;
  /** When true, renders the global account filter in the right cluster. */
  accountScoped: boolean;
}

function TopBar({ active, accountScoped }: TopBarProps) {
  const location = useLocation();
  const [commandOpen, setCommandOpen] = useState(false);

  const isProjectRoute = location.pathname.startsWith('/project/');
  const contextLabel = TOPBAR_CONTEXT_LABEL[active] ?? '';

  // Global ⌘K / Ctrl+K — opens the command palette from anywhere in the app,
  // matching the reference's search-pill kbd chip.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        // Suppress opening the palette over an already-open dialog (drawer,
        // FormModal, ...) — a ⌘K press while e.g. the CR/Task drawer is open
        // must not summon a second overlay on top of/behind it (z-order audit
        // finding 4). CommandBar's own dialog matches this selector too while
        // open, but setCommandOpen(true) is then a no-op, so this never blocks
        // re-focusing an already-open palette.
        if (document.querySelector('[role="dialog"]')) return;
        setCommandOpen(true);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <header
      className="no-print"
      style={{
        // §2.2 — h64 row, no bg fill of its own (paints the page --bg so
        // content scrolling under the sticky bar stays legible), no bottom
        // border, no shadow: separation by spacing.
        height: 'var(--bar-h)',
        background: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '0 24px',
        position: 'sticky',
        top: 0,
        zIndex: 20,
        flexShrink: 0,
      }}
    >
      {/* Left: breadcrumb/page context + the contextual Projects dropdown. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        {contextLabel && (
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 500,
              fontSize: 'var(--text-sm)',
              color: 'var(--text-muted)',
              whiteSpace: 'nowrap',
              paddingRight: 4,
            }}
          >
            {contextLabel}
          </span>
        )}
        <ProjectsMenu isActive={isProjectRoute} />
      </div>

      {/* Right cluster (§2.2): account filter → Create CTA → search pill →
          vertical hairline → bell → user identity cluster. */}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {accountScoped && <AccountSelect />}
        <CreateMenu />
        <SearchPillTrigger onClick={() => setCommandOpen(true)} />
        {/* §2.2 — 1px vertical hairline (~24 tall) between the search pill and the utility cluster. */}
        <span aria-hidden="true" style={{ width: 1, height: 24, background: 'var(--hairline)', flexShrink: 0 }} />
        <NotificationBell />
        <UserMenuCluster />
      </div>

      <CommandBar open={commandOpen} onClose={() => setCommandOpen(false)} />
    </header>
  );
}

// ---------------------------------------------------------------------------
// AppShell
// ---------------------------------------------------------------------------

export interface AppShellProps {
  /** Currently active nav item id. Defaults to "dashboard". */
  active?: string;
  /**
   * Page title — no longer rendered by the chrome itself (every page renders
   * its own h1/breadcrumb). Retained on the prop type so existing route
   * wiring keeps compiling.
   */
  title?: string;
  /** When true, the top-bar shows the global account filter dropdown. */
  accountScoped?: boolean;
  children: ReactNode;
}

/**
 * Application chrome: fixed-left icon rail + slim top bar + full-width
 * content area, offset by the rail's width so nothing overlaps it.
 */
export function AppShell({ active = 'dashboard', accountScoped = false, children }: AppShellProps) {
  const location = useLocation();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        // Deepest layer (§1.1 --canvas) — visible only in the gutter around
        // the floating inset rail; the content column below paints over it
        // with --bg so page content never reads as "sunk into the canvas".
        background: 'var(--canvas)',
        // Content offset for the fixed-left rail: the rail floats inset 10px
        // from the canvas edges (see IconRail) with a further 10px breathing
        // gap before content starts — the rail itself is position:fixed and
        // ignores this, but everything else in the flow (top bar + main)
        // starts after it. Portal-rendered drawers (fixed right) are
        // unaffected, as required.
        paddingLeft: 'calc(var(--rail-w) + 20px)',
      }}
    >
      <SkipLink />
      <IconRail active={active} />

      <TopBar active={active} accountScoped={accountScoped} />

      <main
        id="main-content"
        className="app-grid"
        style={{ flex: 1, background: 'var(--bg)' }}
      >
        <div
          key={location.pathname}
          className="route-enter"
          style={{ maxWidth: 1400, margin: '0 auto', padding: '28px 24px' }}
        >
          {children}
        </div>
      </main>
    </div>
  );
}
